/** Checks that `value` is a non-null, non-array plain object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Type guard for an entity graphic: confirms the value is an object whose
 * optional `customStyle.imageMetadata` chain is also an object when present.
 */
export function isGraphicWithImageData(value: unknown): value is {
  id?: unknown;
  customStyle?: {
    imageMetadata?: {
      imageId?: unknown;
      imageSrc?: unknown;
    };
  };
} {
  if (!isRecord(value)) return false;
  const { customStyle } = value;
  if (customStyle === undefined) return true;
  if (!isRecord(customStyle)) return false;
  const { imageMetadata } = customStyle;
  if (imageMetadata === undefined) return true;
  return isRecord(imageMetadata);
}

/**
 * Type guard for a diagram object: confirms the value has an `entityGraphics`
 * array property.
 */
export function isDiagramWithEntityGraphics(value: unknown): value is {
  entityGraphics: unknown[];
} {
  return isRecord(value) && Array.isArray(value['entityGraphics']);
}

/**
 * Type guard for a DiagramEditor: confirms the value has a `diagram` property
 * whose value, when present, satisfies {@link isDiagramWithEntityGraphics}.
 */
export function hasInternalDiagram(value: unknown): value is {
  diagram?: { entityGraphics: unknown[] };
} {
  if (!isRecord(value) || !('diagram' in value)) return false;
  const { diagram } = value;
  return diagram === undefined || isDiagramWithEntityGraphics(diagram);
}
